import { Nullable, PartialNullable } from "../../Extensions";
import { IPromisePublic, PromiseHelper } from "../../Helpers/PromiseHelper";
import { StringHelper } from "../../Helpers/StringHelper";
import { $$, IJNativeEvent, JNative, JNativeStatic } from "../../JNative";
import { LinqArray } from "../../LinqArray";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyBoolean, PropertyInt32, PropertyObject } from "../../RadObject";
import { PageDimmer } from "../Controls/PageDimmer";
import { IPageManager } from "../PageManager";
import { ContainerControl, ContainerControlPropertyValues, IContainerControl } from "./ContainerControl";
import { Control } from "./Control";
import { NAMESPACE } from "./_Namespace";

import "./Menu.scss";

export type MenuItemPropertyValues =
    ContainerControlPropertyValues
    & {
        key: any;
        toggle: boolean;
        toggleState: boolean;
    };

export type MenuItemProperties = {
    [name in keyof MenuItemPropertyValues]: Property<MenuItemPropertyValues[name]>
};

export interface IMenuItem extends IContainerControl, MenuItemProperties
{
    menuGet(): Nullable<Menu>;
    openAsync(): IPromisePublic<Nullable<MenuItem>>;
}

export class MenuItem<TPropertyValues extends MenuItemPropertyValues = MenuItemPropertyValues, TChildControl extends Control = Control> extends ContainerControl<TPropertyValues, TChildControl> implements IMenuItem
{
    // #region Static

    public static override classNamespace = NAMESPACE + "Menu";
    public static override className = "MenuItem";

    // #region Constructor

    static {
        $$(document).on("click", ".rad-menu > a", (evt: IJNativeEvent) =>
        {
            evt.preventDefault();
            evt.stopPropagation();

            const
                $$target = $$(evt.currentTarget),
                menu = $$target.closest(".rad-menu").dataGet("rad-control-menu-item") as Menu | undefined,
                menuItem = $$target.dataGet("rad-control-menu-item") as MenuItem | undefined;

            if (!menu)
                return;

            if (!menuItem)
            {
                if ($$target.hasClass("rad-action-back"))
                {
                    menu._menuItemSelected(null);
                    if (menu.$$menuElement)
                        Menu.dimmer?.hideElementOrControlAsync(menu.$$menuElement);
                }
                return;
            }

            if (menuItem.controlsGet().length)
            {
                menuItem.openAsync()
                    .then(childMenuItem =>
                    {
                        if (!childMenuItem) // Back clicked
                            return;

                        menu._menuItemSelected(childMenuItem);
                    });
            }
            else
            {
                Menu.dimmer
                    ?.hideAllAsync()
                    .then(() =>
                    {
                        menu._menuItemSelected(menuItem);
                    });
            }
        });
        $$(document).on("click", ".rad-control-page-dimmer-menu", (evt: IJNativeEvent) =>
        {
            evt.preventDefault();
            evt.stopPropagation();

            /* INFO: We're about to close all menus without selecting an option
                   so we need to resolve and null the promise property of each menu
                in the dimmer
                    */
            $$(evt.currentTarget)
                .children()
                .each((index, element) =>
                {
                    const
                        menuItem = $$(element).dataGet("rad-control-menu-item");

                    if (menuItem.openPromise)
                    {
                        menuItem.openPromise.resolve(null);
                        menuItem.openPromise = null;
                    }
                });

            Menu.dimmer
                ?.hideAllAsync();
        });
    }

    // #endregion

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<MenuItemPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                toggle: false,
                toggleState: false,
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected _$$menuItemElement?: JNative<HTMLElement>;
    protected $$menuElement?: JNative<HTMLElement>;
    protected openPromise?: IPromisePublic<Nullable<MenuItem>>;
    protected updateMenuElementTimeoutId: number;

    // #endregion

    // #region Properties

    public key!: PropertyObject;
    public toggle!: PropertyBoolean;
    public toggleState!: PropertyBoolean;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<TPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-menu-item");
        this.updateMenuElementTimeoutId = 0;
    }

    // #endregion

    // #region Public Methods

    public menuGet(): Nullable<Menu>
    {
        if (this instanceof Menu)
            return this;

        return this.$$element.get()?.closest(".rad-control-menu").radControl() as Nullable<Menu>;
    }
    public openAsync(): IPromisePublic<Nullable<MenuItem>>
    {
        if (!this.$$menuElement)
            throw new Error("$$menuElement not set");

        if (!Menu.dimmer)
            throw new Error("Menu dimmer not set");

        if (!this.openPromise)
        {
            this.openPromise = PromiseHelper.createPromisePublic<Nullable<MenuItem>>();
            Menu.dimmer.showElementOrControlAsync(this.$$menuElement);
        }

        return this.openPromise;
    }

    // #endregion

    // #region Protected Methods

    protected _keyChanged(change: PropertyChange<any>)
    {
        /* Intentionally empty */
    }
    protected _menuItemSelected(menuItem: Nullable<MenuItem>)
    {
        if (!this.openPromise)
            return;

        if (menuItem?.toggle.get())
            menuItem.toggleState.set(!menuItem.toggleState.get());

        this.openPromise.resolve(menuItem);
        delete this.openPromise;
    }
    protected _toggleChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _toggleStateChanged(change: PropertyChange<boolean>)
    {
        const
            menu = this.menuGet();

        if (menu)
            menu._updateMenuElement();
    }
    protected _updateMenuElement()
    {
        this.$$menuElement?.empty();

        if (this instanceof Menu)
        {
            this.$$menuElement?.append(
                JNativeStatic.create$$({
                    nodeName: "span",
                    attributes: { "class": "item icon" },
                    children: [{
                        nodeName: "i",
                        attributes: { "class": StringHelper.format("icon{0}", this.toggle.get() && this.toggleState.get() ? " check" : StringHelper.empty) }
                    },
                    JNativeStatic.createText(this.text.get() ?? StringHelper.empty)]
                })
            );
        }
        else
        {
            this.$$menuElement?.append(
                JNativeStatic.create$$({
                    nodeName: "a",
                    attributes: { "class": "item icon rad-action-back", href: "#" },
                    children: [{
                        nodeName: "i",
                        attributes: {
                            "class": StringHelper.format("icon{0} angle double left", this.toggle.get() && this.toggleState.get() ? " check" : StringHelper.empty)
                        }
                    },
                    JNativeStatic.createText(this.text.get() ?? StringHelper.empty)]
                })
            );
        }

        this.controlsGet().forEach(item =>
        {
            const
                menuItem = item as unknown as MenuItem<TPropertyValues, MenuItem>,
                hasChildMenuItems = menuItem.controlsGet().length > 0,
                $$menuItemElement =
                    $$(
                        StringHelper.formatWithHtmlEncode(
                            "<a href=\"#\" class=\"item icon\"><i class=\"icon{0}\"></i>{1}</a>",
                            menuItem.toggle.get() && menuItem.toggleState.get() ? " check" : StringHelper.empty,
                            menuItem.text.get()
                        )
                    );

            if (hasChildMenuItems)
                $$menuItemElement.append("<i class=\"icon dropdown\"></i>");

            $$menuItemElement.data("rad-control-menu-item", menuItem);
            menuItem._$$menuItemElement = $$menuItemElement;

            this.$$menuElement?.append($$menuItemElement);
            menuItem.updateVisibility();
        });
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: TPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.key = new PropertyObject(this, propertyValues.key, this._keyChanged);
        this.toggle = new PropertyBoolean(this, propertyValues.toggle, this._toggleChanged);
        this.toggleState = new PropertyBoolean(this, propertyValues.toggleState, this._toggleStateChanged);
    }

    protected override _createElement()
    {
        const
            $$element = super._createElement();

        this.$$menuElement = JNativeStatic.create$$({
            nodeName: "div",
            attributes: { "class": "rad-menu ui menu vertical" }
        });
        this.$$menuElement.data("rad-control-menu-item", this);
        this._updateMenuElement();

        return $$element;
    }

    public override append($$elementOrControlOrArrayOf: JNative | TChildControl | TChildControl[], beforeSelector?: any)
    {
        if (this.updateMenuElementTimeoutId)
            clearTimeout(this.updateMenuElementTimeoutId);

        super.append($$elementOrControlOrArrayOf, beforeSelector);
        this.updateMenuElementTimeoutId = window.setTimeout(() => { this._updateMenuElement(); });

        return this;
    }
    public override controlsClear()
    {
        super.controlsClear();
        this._updateMenuElement();
    }
    public override prepend($$elementOrControlOrArrayOf: JNative | TChildControl | TChildControl[], beforeSelector?: any)
    {
        super.prepend($$elementOrControlOrArrayOf, beforeSelector);
        this._updateMenuElement();
        return this;
    }
    public override updateVisibility()
    {
        super.updateVisibility();

        if (!this._$$menuItemElement)
            return;

        if (this.isVisible.get())
            this._$$menuItemElement.show();
        else
            this._$$menuItemElement.hide();
    }

    // #endregion

    // #endregion
}

export type MenuPropertyValues =
    MenuItemPropertyValues
    & {
        visibleMenuItemCount: number;
    };

export type MenuProperties = {
    [name in keyof MenuPropertyValues]: Property<MenuPropertyValues[name]>
};

export interface IMenu extends IMenuItem, MenuProperties
{
}

type TChildControl = MenuItem;

export class Menu<TPropertyValues extends MenuPropertyValues = MenuPropertyValues> extends MenuItem<TPropertyValues, TChildControl> implements IMenu
{
    // #region Static

    public static override classNamespace = NAMESPACE + "Menu";
    public static override className = "Menu";

    // #region Global Variables

    public static dimmer?: PageDimmer;

    // #endregion

    // #region Public Methods

    public static override getAllowedChildControlClasses()
    {
        return [MenuItem];
    }

    // #endregion

    // #region Constructor

    static {

        $$(() =>
        {
            Menu.dimmer = new PageDimmer({ animationIn: "scale in", animationOut: "scale out", cssClass: "rad-control-page-dimmer-menu", repositionChildren: true });
        });

        $$(document).on("SeriouslyRAD.PageManager:beforeInitialize", (evt: IJNativeEvent, pageManager: IPageManager) =>
        {
            pageManager.registerBlockNavigationFunction(() =>
            {
                return Menu.dimmer?.shown() ?? false;
            });
        });
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Properties

    public visibleMenuItemCount!: PropertyInt32;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<TPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-menu");
        this.detach();
    }

    // #endregion

    // #region Public Methods

    public override controlsClear()
    {
        (this.controlsGet() as MenuItem[])
            .forEach(item => { this._unBindMenuItemVisibilityUpdated(item); });

        super.controlsClear();

        this._updateVisibleMenuItemCount();
    }

    // #endregion

    // #region Protected Methods

    protected _bindMenuItemVisibilityUpdated(menuItem: MenuItem)
    {
        if (!menuItem)
            return;

        menuItem.on("isVisibleChanged", { self: this }, this.onMenuItemIsVisibleChanged);
        this._updateVisibleMenuItemCount();
    }
    protected _unBindMenuItemVisibilityUpdated(menuItem: MenuItem)
    {
        if (!menuItem)
            return;

        menuItem.off("isVisibleChanged", { self: this }, this.onMenuItemIsVisibleChanged);
    }
    protected _updateVisibleMenuItemCount()
    {
        this.visibleMenuItemCount.set(
            LinqArray.create(this.controlsGet())
                .count(item => !!item.isVisible.get())
        );
    }
    protected _visibleMenuItemCountChanged(change: PropertyChange<number>)
    {
        this.triggerHandler("visibleMenuItemCountChanged", change);
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: TPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.visibleMenuItemCount = new PropertyInt32(this, propertyValues.visibleMenuItemCount, this._visibleMenuItemCountChanged);
    }

    public override append(menuItem: MenuItem)
    {
        this._bindMenuItemVisibilityUpdated(menuItem);

        const
            result = super.append(menuItem);

        this._updateVisibleMenuItemCount();
        return result;
    }
    public override prepend(menuItem: MenuItem)
    {
        this._bindMenuItemVisibilityUpdated(menuItem);

        const
            result = super.prepend(menuItem);

        this._updateVisibleMenuItemCount();
        return result;
    }
    //public override remove(menuItem: MenuItem)
    //{
    //    this._unBindMenuItemVisibilityUpdated(menuItem);
    //    super.remove(menuItem);
    //    this._updateVisibleMenuItemCount();
    //}

    // #endregion

    // #region Event Handlers

    protected onMenuItemIsVisibleChanged(evt: IJNativeEvent, change: PropertyChange<boolean>)
    {
        const
            self = evt.data.self as this;

        self._updateVisibleMenuItemCount();
    }

    // #endregion

    // #endregion
}

